import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { required, validationContext } from 'validx'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * View model for the dialog that creates a legend.
 */
export class NewLegendDialogViewModel {
  @observable accessor name = ''

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  readonly validation = validationContext(this, {
    name: [required('Enter a name for the legend.')],
  })

  constructor(
    private readonly _createLegend: (name: string) => Promise<unknown>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether a name has been entered.
   */
  @computed
  get canCreate() {
    return !!this.name.trim()
  }

  /**
   * Clears the name and its errors.
   */
  @action.bound
  reset() {
    this.name = ''
    this.validation.reset()
  }

  /**
   * Creates the legend.
   */
  create = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    await this._createLegend(this.name.trim()).catch(
      this._createAlertErrorHandler('Creating the legend failed'),
    )
    this.modal.close()
  })
}
